import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Upload, Loader2, Plus, Trash2, Download, Save, FileSpreadsheet,
  Sparkles, Search, Check, AlertTriangle, GitBranch, FileText, Send, BookOpen, RefreshCw,
  ArrowRight, Pencil, LayoutList, ChevronDown, Eye, FilePlus2, Layers,
} from "lucide-react";
import api, { API, apiError, openDownload } from "@/lib/api";
import { money, statusMeta, confidenceMeta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { QuotePdfPages } from "@/components/QuotePdfPages";
import { PriceAutomaticallyButton } from "@/components/PriceAutomaticallyButton";

const MATCH_STEPS = [
  "Reading your BOQ workbook...",
  "Detecting columns and tabs...",
  "Importing line items...",
];

function MatchSwap({ item, catalogue, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`swap-match-${item.id}`}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> {item.matched_item_id ? "Change" : "Match"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[340px] bg-card" align="start">
        <Command className="bg-card">
          <CommandInput placeholder="Search catalogue..." />
          <CommandList>
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {catalogue.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.item_code} ${c.description} ${c.category}`}
                  onSelect={() => { onPick(item.id, c); setOpen(false); }}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm truncate">{c.description}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{c.item_code} · {money(c.unit_rate, "")}</div>
                  </div>
                  {item.matched_item_id === c.id && <Check className="w-4 h-4 text-primary shrink-0" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function RfqDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [meta, setMeta] = useState(null);
  const [lines, setLines] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [matchStep, setMatchStep] = useState(-1);
  const [boqInfo, setBoqInfo] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [revising, setRevising] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [matching, setMatching] = useState(false);
  const [buyerRfqId, setBuyerRfqId] = useState("");
  const [savedCat, setSavedCat] = useState({});
  const [activeTab, setActiveTab] = useState("__summary__");
  const [editingTab, setEditingTab] = useState(null);
  const [tabDraft, setTabDraft] = useState("");
  // Quotation supplements: templates, collapsible, save-as-template & preview
  const [templates, setTemplates] = useState([]);
  const [applyingTpl, setApplyingTpl] = useState(false);
  const [supplementsOpen, setSupplementsOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [newTplName, setNewTplName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/rfqs/${id}`).then((r) => {
      setRfq(r.data);
      setMeta(pickMeta(r.data));
      setLines(r.data.line_items || []);
      setBuyerRfqId(r.data.source_buyer_rfq_id || "");
    }).catch(() => { toast.error("RFQ not found"); navigate("/dashboard"); });
    api.get("/catalogue").then((r) => setCatalogue(r.data)).catch(() => {});
    api.get(`/rfqs/${id}/revisions`).then((r) => setRevisions(r.data)).catch(() => {});
    api.get("/quote-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [id]);

  function pickMeta(d) {
    return {
      name: d.name, buyer_name: d.buyer_name, buyer_address: d.buyer_address,
      quote_ref: d.quote_ref, status: d.status, tax_percent: d.tax_percent,
      currency: user?.currency || d.currency, notes: d.notes, terms: d.terms, validity_days: d.validity_days,
      intro: d.intro || "", payment_terms: d.payment_terms || "", bank_details: d.bank_details || "",
      signatory_name: d.signatory_name || "", signatory_title: d.signatory_title || "",
      discount_type: d.discount_type || "percent", discount_value: d.discount_value || 0,
      cover_mode: d.cover_mode || "default",
    };
  }

  const cur = meta?.currency || "$";
  const priced = lines.filter((l) => (l.kind || "item") === "item");

  const sendQuotation = async () => {
    setSending(true);
    try {
      await save();
      const { data } = await api.post(`/vendor/opportunities/${buyerRfqId}/submit`);
      toast.success(`Quotation sent to the contractor — ${cur} ${Number(data.total).toLocaleString()}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the quotation", { duration: 7000 });
    } finally {
      setSending(false);
    }
  };

  const quoteWarnings = () => {
    const w = [];
    if (!meta.buyer_name) w.push("Client / contractor name is missing.");
    if (priced.length === 0) w.push("This quotation has no line items.");
    const unpriced = priced.filter((l) => !l.rate_only && !(Number(l.unit_rate) > 0)).length;
    if (unpriced > 0) w.push(`${unpriced} line item(s) have no rate yet.`);
    if (!user?.company_name) w.push("Your company name is missing (Vendor Profile).");
    if (!user?.address) w.push("Your company address is missing (Vendor Profile).");
    if (!user?.has_logo) w.push("No company logo (Vendor Profile).");
    return w;
  };
  const previewQuotation = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl("");
    try {
      const ok = await save(true);
      if (!ok) { setPreviewOpen(false); return; }
      const { data } = await api.get(`/rfqs/${id}/pdf`, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(new Blob([data], { type: "application/pdf" })));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build the preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setPreviewOpen(false);
  };

  const applyTemplate = async (templateId) => {
    if (!templateId) return;
    setApplyingTpl(true);
    try {
      const { data } = await api.post(`/rfqs/${id}/apply-template/${templateId}`);
      const a = data.applied || {};
      setMeta((m) => ({ ...m, intro: a.intro, terms: a.terms, payment_terms: a.payment_terms,
        bank_details: a.bank_details, notes: a.notes, validity_days: a.validity_days,
        tax_percent: a.tax_percent, cover_mode: a.cover_mode }));
      setRfq((r) => ({ ...r, cover_pdf: a.cover_pdf, closing_pdf: a.closing_pdf, cover_mode: a.cover_mode }));
      setSupplementsOpen(true);
      toast.success(`Autofilled from "${data.template_name || "template"}"`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not apply that template");
    } finally {
      setApplyingTpl(false);
    }
  };

  const saveAsTemplate = async () => {
    const name = newTplName.trim();
    if (!name) { toast.error("Give the template a name"); return; }
    setSavingTpl(true);
    try {
      await save(true);
      await api.post("/quote-templates", {
        name, intro: meta.intro, terms: meta.terms, payment_terms: meta.payment_terms,
        bank_details: meta.bank_details, notes: meta.notes,
        validity_days: parseInt(meta.validity_days) || 30,
        copy_pages_from_rfq: id,
      });
      const { data } = await api.get("/quote-templates");
      setTemplates(data);
      setSaveTplOpen(false);
      setNewTplName("");
      toast.success(`Saved "${name}" — reuse it on any quote`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save the template");
    } finally {
      setSavingTpl(false);
    }
  };

  const requestSend = () => setConfirmOpen(true);
  const confirmSend = async () => { setConfirmOpen(false); await sendQuotation(); };

  const lineAmount = (li) =>
    (li.kind || "item") !== "item" || li.rate_only
      ? 0
      : (Number(li.quantity) || 0) * (Number(li.unit_rate) || 0);

  // sub-total per buyer page / tab, in document order
  const pageTotals = useMemo(() => {
    const map = new Map();
    lines.forEach((li) => {
      const key = `${li.sheet || ""}||${li.page || ""}`;
      map.set(key, (map.get(key) || 0) + lineAmount(li));
    });
    const out = [...map.entries()].map(([k, v]) => {
      const [sheet, page] = k.split("||");
      return { sheet, page, subtotal: v };
    });
    if (out.length <= 1 && !out[0]?.page) return [];
    return out;
  }, [lines]);

  const pageSubtotal = (sheet, page) =>
    pageTotals.find((p) => p.sheet === (sheet || "") && p.page === (page || ""))?.subtotal ?? null;

  const totals = useMemo(() => {
    const sub = lines.reduce((s, li) => s + lineAmount(li), 0);
    const dv = parseFloat(meta?.discount_value) || 0;
    let discount = meta?.discount_type === "amount" ? dv : (sub * dv) / 100;
    discount = Math.max(0, Math.min(discount, sub));
    const net = sub - discount;
    const tax = net * ((parseFloat(meta?.tax_percent) || 0) / 100);
    return { sub, discount, net, tax, total: net + tax };
  }, [lines, meta]);

  // --- BOQ tabs: one per uploaded sheet, plus a Summary tab ---
  const sheetOrder = useMemo(() => {
    const seen = [];
    let hasBlank = false;
    lines.forEach((l) => {
      const s = l.sheet || "";
      if (s) { if (!seen.includes(s)) seen.push(s); }
      else if ((l.kind || "item") === "item") hasBlank = true;
    });
    if (seen.length && hasBlank) seen.push("");  // ungrouped lines get an "Other" tab when tabbed
    return seen;
  }, [lines]);
  const tabbed = sheetOrder.length > 1;
  const detailSheets = sheetOrder;
  const tabLabel = (n) => (n === "" ? "Other" : n);
  const sheetTotal = (name) =>
    lines.filter((l) => (l.sheet || "") === name).reduce((s, l) => s + lineAmount(l), 0);
  const detailGrand = detailSheets.reduce((s, n) => s + sheetTotal(n), 0);
  const showSummaryPanel = tabbed && activeTab === "__summary__";
  const showTable = !tabbed || activeTab !== "__summary__";
  const viewLines = !tabbed
    ? lines
    : activeTab === "__summary__"
      ? []
      : lines.filter((l) => (l.sheet || "") === activeTab);

  useEffect(() => {
    if (!tabbed) return;
    const valid = ["__summary__", ...detailSheets];
    if (!valid.includes(activeTab)) setActiveTab("__summary__");
  }, [tabbed, detailSheets.join("|"), activeTab]);

  // A rate is only "missing" when left blank — 0 is a valid, intentional rate.
  const rateMissing = (r) => r === "" || r === null || r === undefined || Number.isNaN(Number(r));

  const missingRates = priced.filter(
    (l) => (l.matched_description || l.boq_description) && rateMissing(l.unit_rate)
  ).length;

  // Manually priced lines that aren't linked to the catalogue yet (a nudge to save them).
  const canAddToCatalogue = (li) =>
    (li.kind || "item") === "item" && !li.matched_item_id &&
    !!(li.matched_description || li.boq_description) && Number(li.unit_rate) > 0;
  const manualUnsaved = priced.filter((l) => canAddToCatalogue(l) && !savedCat[l.id]).length;

  const addToCatalogue = async (li) => {
    const description = (li.matched_description || li.boq_description || "").trim();
    if (!description) return toast.error("Add a description before saving to the catalogue");
    try {
      const { data } = await api.post("/catalogue", {
        description, unit: li.unit || "", item_code: li.item_code || "",
        unit_rate: Number(li.unit_rate) || 0,
      });
      setSavedCat((s) => ({ ...s, [li.id]: true }));
      setCatalogue((prev) => [...prev, data]);
      updateLine(li.id, { matched_item_id: data.id, source: "catalogue", confidence: 100 });
      toast.success("Saved to your catalogue — it'll auto-match next time");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save to catalogue");
    }
  };

  const updateLine = (lid, patch) =>
    setLines((prev) => prev.map((l) => (l.id === lid ? { ...l, ...patch } : l)));

  const pickMatch = (lid, c) =>
    updateLine(lid, {
      matched_item_id: c.id, matched_description: c.description, item_code: c.item_code,
      unit_rate: c.unit_rate, unit: c.unit, confidence: 100, source: "catalogue",
    });

  const nextSheet = () =>
    !tabbed ? "" : activeTab !== "__summary__" ? activeTab : (detailSheets.find((s) => s) || "");

  const addManual = () => {
    const sh = nextSheet();
    setLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), boq_description: "", matched_description: "", item_code: "",
        unit: "", quantity: 1, unit_rate: 0, confidence: 0, source: "manual", rate_only: false,
        row_index: null, kind: "item", level: 0, remarks: "", rate_note: "", header_path: "",
        sheet: sh, page: "" },
    ]);
    if (tabbed && activeTab === "__summary__" && sh) setActiveTab(sh);
  };

  const addSection = () => {
    const sh = nextSheet();
    setLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), boq_description: "New section", matched_description: "", item_code: "",
        unit: "", quantity: 0, unit_rate: 0, confidence: 0, source: "manual", rate_only: false,
        row_index: null, kind: "header", level: 0, remarks: "", rate_note: "", header_path: "",
        sheet: sh, page: "" },
    ]);
    if (tabbed && activeTab === "__summary__" && sh) setActiveTab(sh);
  };

  const removeLine = (lid) => setLines((prev) => prev.filter((l) => l.id !== lid));

  const insertAfter = (lid) =>
    setLines((prev) => {
      const i = prev.findIndex((l) => l.id === lid);
      if (i < 0) return prev;
      const src = prev[i];
      const row = {
        id: crypto.randomUUID(), boq_description: "", matched_description: "", item_code: "",
        unit: "", quantity: 1, unit_rate: 0, confidence: 0, source: "manual", rate_only: false,
        row_index: null, kind: "item", level: Number(src.level) || 0, remarks: "", rate_note: "",
        header_path: src.header_path || "", sheet: src.sheet || "", page: src.page || "",
      };
      return [...prev.slice(0, i + 1), row, ...prev.slice(i + 1)];
    });

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/rfqs/${id}`, {
        ...meta,
        tax_percent: parseFloat(meta.tax_percent) || 0,
        discount_value: parseFloat(meta.discount_value) || 0,
        line_items: lines,
      });
      setRfq(data);
      if (!silent) toast.success("Saved");
      return true;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const commitRename = async (oldName) => {
    const nn = tabDraft.trim();
    setEditingTab(null);
    if (!nn || nn === oldName) return;
    if (detailSheets.includes(nn)) { toast.error("A tab with that name already exists"); return; }
    const ok = await save(true);
    if (!ok) return;
    try {
      const { data } = await api.post(`/rfqs/${id}/rename-sheet`, { old_name: oldName, new_name: nn });
      setRfq(data);
      setLines(data.line_items || []);
      setMeta(pickMeta(data));
      setActiveTab(nn);
      toast.success("Tab renamed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not rename tab");
    }
  };

  const onBoqUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMatchStep(0);
    const stepper = setInterval(() => setMatchStep((s) => (s < MATCH_STEPS.length - 1 ? s + 1 : s)), 900);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/rfqs/${id}/upload-boq`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRfq(data);
      setLines(data.line_items || []);
      setActiveTab("__summary__");
      setMeta(pickMeta(data));
      const ms = data.match_summary;
      setBoqInfo(ms);
      toast.success(
        `Imported ${ms.total} line item${ms.total === 1 ? "" : "s"}` +
        (ms.sheets?.length > 1 ? ` across ${ms.sheets.length} tabs` : "") +
        ". Now price them — Price Automatically, Price from file, or enter rates manually."
      );
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      clearInterval(stepper);
      setUploading(false);
      setMatchStep(-1);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const rematchAll = async () => {
    if (catalogue.length === 0) {
      toast.error("Your catalogue is empty — add items first so BOQ lines can match");
      return;
    }
    const ok = await save(true);
    if (!ok) return;
    setMatching(true);
    try {
      const { data } = await api.post(`/rfqs/${id}/auto-price?rematch=true`);
      setLines(data.line_items || []);
      setMeta(pickMeta(data));
      const ms = data.match_summary;
      setBoqInfo(ms);
      if (ms.ai_skipped) {
        toast.warning(`Matching skipped — your ${ms.plan_name} plan's ${ms.quota} automatic quotes are used up this month`);
      } else {
        toast.success(`Priced ${ms.matched} of ${ms.total} line item(s) from your catalogue${tabbed ? ` across ${detailSheets.length} tab${detailSheets.length === 1 ? "" : "s"}` : ""}`);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not re-match the BOQ");
    } finally {
      setMatching(false);
    }
  };

  const loadRevisions = async () => {
    try {
      const { data } = await api.get(`/rfqs/${id}/revisions`);
      setRevisions(data);
    } catch { /* ignore */ }
  };

  const newRevision = async () => {
    const ok = await save(true);
    if (!ok) return;
    setRevising(true);
    try {
      const { data } = await api.post(`/rfqs/${id}/revisions`);
      setRfq(data);
      toast.success(`Revision ${data.revision - 1} frozen — you are now editing Rev ${data.revision}`);
      loadRevisions();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRevising(false);
    }
  };

  const downloadPdf = async () => {
    const ok = await save(true);
    if (!ok) return;
    await openDownload(`/rfqs/${id}/pdf`);
  };

  const downloadExcel = async () => {
    const ok = await save(true);
    if (!ok) return;
    await openDownload(`/rfqs/${id}/excel`);
  };

  if (!rfq || !meta) {
    return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const baseRef = (meta.quote_ref || "").trim();
  const fullRef = /-\s*(r|rev)\s*\.?\s*\d+\s*$/i.test(baseRef)
    ? baseRef
    : `${baseRef}-R${rfq.revision || 1}`;

  return (
    <div>
      <button data-testid="back-button" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <input
              data-testid="quote-ref-input"
              value={meta.quote_ref || ""}
              onChange={(e) => setMeta({ ...meta, quote_ref: e.target.value })}
              title="Quotation reference — type your own if you prefer"
              className="font-mono text-xs text-muted-foreground bg-transparent outline-none rounded px-1 -ml-1 focus:bg-secondary w-40"
            />
            <span data-testid="quote-ref-display" className="font-mono text-xs font-bold text-primary">
              {fullRef}
            </span>
            <span data-testid="revision-badge" className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wide">
              Rev {rfq.revision || 1}
            </span>
            {rfq.sheets?.length > 1 && (
              <span className="text-[11px] text-muted-foreground">{rfq.sheets.length} tabs</span>
            )}
          </div>
          <input
            data-testid="rfq-title-input"
            value={meta.name}
            onChange={(e) => setMeta({ ...meta, name: e.target.value })}
            className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-slate-100 bg-transparent outline-none w-full focus:bg-secondary rounded px-1 -ml-1"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={meta.status} onValueChange={(v) => setMeta({ ...meta, status: v })}>
            <SelectTrigger data-testid="status-select" className="w-32 h-11 bg-card"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="new-revision-button" variant="outline" onClick={newRevision} disabled={revising} className="h-11 gap-2">
            {revising ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitBranch className="w-4 h-4" />} New Revision
          </Button>
          <Button data-testid="save-rfq-button" variant="outline" onClick={() => save()} disabled={saving} className="h-11 gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </Button>
          <Button data-testid="download-excel-button" variant="outline" onClick={downloadExcel} className="h-11 gap-2">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button data-testid="preview-quotation-button" variant="outline" onClick={previewQuotation} className="h-11 gap-2 font-semibold">
            <Eye className="w-4 h-4" /> Preview
          </Button>
          <Button data-testid="download-pdf-button" onClick={downloadPdf} className="h-11 gap-2 font-semibold">
            <Download className="w-4 h-4" /> PDF
          </Button>
          {buyerRfqId && (
            <Button data-testid="send-quotation-button" onClick={requestSend} disabled={sending} className="h-11 gap-2 font-semibold">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Quotation
            </Button>
          )}
        </div>
      </div>
      <div className="mb-6">
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-semibold">Contractor Details</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Contractor Name</Label>
              <Input data-testid="buyer-name-input" value={meta.buyer_name} onChange={(e) => setMeta({ ...meta, buyer_name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs">Contractor Address</Label>
              <Input value={meta.buyer_address} onChange={(e) => setMeta({ ...meta, buyer_address: e.target.value })} className="mt-1.5" />
            </div>
          </div>
        </div>
      </div>

      {/* BOQ upload / AI matching */}
      <AnimatePresence>
        {uploading && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mb-6 bg-primary/5 border border-primary/20 rounded-xl p-5 overflow-hidden">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary animate-pulse" />
              <span className="font-semibold text-sm text-primary">Importing your BOQ...</span>
            </div>
            <div className="space-y-2">
              {MATCH_STEPS.map((s, i) => (
                <div key={s} className={`flex items-center gap-2 text-sm transition-colors ${i <= matchStep ? "text-foreground" : "text-muted-foreground/50"}`}>
                  {i < matchStep ? <Check className="w-4 h-4 text-emerald-500" /> : i === matchStep ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <div className="w-4 h-4 rounded-full border border-current opacity-40" />}
                  {s}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Line items */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden mb-6">
        {(catalogue.length === 0 || (priced.length > 0 && missingRates === priced.length && !boqInfo?.imported_only)) && (
          <div data-testid="no-match-warning" className="px-5 py-3 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-900/50 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {catalogue.length === 0
                ? "Your catalogue is empty, so no rates can be pulled. Go to Catalogue → Import Excel to load your price list."
                : `None of these BOQ lines matched your ${catalogue.length}-item catalogue, so no rates were pulled. Import the price list that covers this package, or set rates manually below.`}
            </span>
          </div>
        )}
        {boqInfo?.ai_skipped && (
          <div data-testid="quota-exhausted-banner" className="px-5 py-3 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">
              AI matching was skipped — you have used all {boqInfo.quota} automatic quotes on the {boqInfo.plan_name} plan
              this month. Every BOQ line was imported so you can price it manually.
            </span>
            <button
              data-testid="quota-upgrade-link"
              onClick={() => navigate("/billing")}
              className="font-bold underline whitespace-nowrap"
            >
              Upgrade plan
            </button>
          </div>
        )}
        {catalogue.length === 0 && (
          <div data-testid="empty-catalogue-reminder" className="px-5 py-3 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-900/50 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">
              Your catalogue is empty, so BOQ lines can't auto-match. Add items in the Catalogue to unlock automatic pricing, use “Price from file” to match against a price list you upload, or price each line manually below.
            </span>
            <button data-testid="empty-catalogue-link" onClick={() => navigate("/catalogue")} className="font-bold underline whitespace-nowrap">
              Go to Catalogue
            </button>
          </div>
        )}
        {manualUnsaved > 0 && (
          <div data-testid="add-to-catalogue-reminder" className="px-5 py-3 bg-blue-50 dark:bg-blue-950/20 border-b border-blue-200 dark:border-blue-900/50 text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
            <BookOpen className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {manualUnsaved} item{manualUnsaved === 1 ? "" : "s"} priced manually {manualUnsaved === 1 ? "isn't" : "aren't"} in your catalogue yet.
              Use <span className="font-semibold">“Add to catalogue”</span> on a line to save the rate and auto-match it next time.
            </span>
          </div>
        )}
        {boqInfo && (
          <div data-testid="boq-mapping-info" className="px-5 py-3 bg-primary/5 border-b border-primary/20 text-xs text-muted-foreground flex flex-wrap gap-x-5 gap-y-1">
            <span>Detected columns → <span className="font-semibold text-foreground">Description:</span> {boqInfo.columns?.description}</span>
            <span><span className="font-semibold text-foreground">Quantity:</span> {boqInfo.columns?.quantity}</span>
            {boqInfo.columns?.unit && <span><span className="font-semibold text-foreground">Unit:</span> {boqInfo.columns.unit}</span>}
            <span>{boqInfo.imported_only
              ? `${boqInfo.total} line items imported`
              : `${boqInfo.matched || 0} of ${boqInfo.total} priced lines matched`} · {boqInfo.headers || 0} header row(s) · {boqInfo.pages || 0} page(s)
              {boqInfo.sheets?.length > 1 && ` · tabs: ${boqInfo.sheets.join(", ")}`}
              {boqInfo.skipped_sheets?.length > 0 && ` · skipped tabs: ${boqInfo.skipped_sheets.join(", ")}`}
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <div>
            <h3 className="font-display font-bold text-slate-900 dark:text-slate-100">Line Items</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {priced.length} priced item{priced.length === 1 ? "" : "s"}
              {lines.length - priced.length > 0 && ` · ${lines.length - priced.length} section header(s)`}
              {missingRates > 0 && <span className="text-rose-600 dark:text-rose-400"> · {missingRates} missing rate</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onBoqUpload} data-testid="boq-file-input" />
            <span title={buyerRfqId ? "This quotation is based on a contractor's RFQ — its line items come from the RFQ and can't be replaced by uploading a BOQ." : ""}>
              <Button variant="outline" data-testid="upload-boq-button" onClick={() => fileRef.current?.click()} disabled={uploading || !!buyerRfqId} className="gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload BOQ
              </Button>
            </span>
            {lines.some((l) => l.kind === "item") && (
              <PriceAutomaticallyButton
                variant="outline"
                className="gap-2"
                testid="price-automatically-button"
                align="end"
                catEmpty={catalogue.length === 0}
                disabled={matching || uploading}
                getRfqId={async () => { const ok = await save(true); return ok ? id : null; }}
                onCatalogue={rematchAll}
                onPriced={(data) => { setLines(data.line_items || []); setMeta(pickMeta(data)); setBoqInfo(data.match_summary); }}
              />
            )}
            <Button variant="ghost" data-testid="add-section-button" onClick={addSection} className="gap-2">
              <Plus className="w-4 h-4" /> Add Section
            </Button>
            <Button variant="outline" data-testid="add-line-button" onClick={addManual} className="gap-2">
              <Plus className="w-4 h-4" /> Add Item
            </Button>
          </div>
        </div>

        {lines.length === 0 ? (
          <div className="py-20 text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground/40" />
            <p className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No line items yet</p>
            {buyerRfqId ? (
              <>
                <p className="text-sm text-muted-foreground mt-1">This quotation is based on a contractor's RFQ. Add items manually to price it.</p>
                <Button className="mt-5 gap-2" onClick={addManual}>
                  <Plus className="w-4 h-4" /> Add Item
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mt-1">Upload a contractor's BOQ Excel to auto-match items, or add manually.</p>
                <Button className="mt-5 gap-2" onClick={() => fileRef.current?.click()}>
                  <Upload className="w-4 h-4" /> Upload BOQ
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
          {tabbed && (
            <div className="flex items-center gap-1 px-3 pt-3 overflow-x-auto border-b border-border" data-testid="boq-tabs">
              {[{ n: "__summary__", l: rfq.summary_sheet || "Summary" }, ...detailSheets.map((s) => ({ n: s, l: s }))].map((t) => {
                const active = activeTab === t.n;
                const isDetail = t.n !== "__summary__";
                if (isDetail && editingTab === t.n) {
                  return (
                    <input
                      key={t.n}
                      autoFocus
                      data-testid="tab-rename-input"
                      value={tabDraft}
                      onChange={(e) => setTabDraft(e.target.value)}
                      onBlur={() => commitRename(t.n)}
                      onKeyDown={(e) => { if (e.key === "Enter") commitRename(t.n); if (e.key === "Escape") setEditingTab(null); }}
                      className="h-9 px-3 rounded-t-lg border border-border border-b-0 text-sm font-semibold bg-card w-44 outline-none focus:border-primary"
                    />
                  );
                }
                return (
                  <button
                    key={t.n}
                    data-testid={`boq-tab-${isDetail ? t.n : "summary"}`}
                    onClick={() => setActiveTab(t.n)}
                    onDoubleClick={() => { if (isDetail) { setEditingTab(t.n); setTabDraft(t.n); } }}
                    title={isDetail ? "Double-click to rename" : ""}
                    className={`group flex items-center gap-1.5 h-9 px-3 rounded-t-lg text-sm font-semibold whitespace-nowrap border border-b-0 transition-colors ${active ? "bg-card border-border text-primary -mb-px" : "bg-transparent border-transparent text-muted-foreground hover:text-foreground"}`}
                  >
                    {!isDetail && <LayoutList className="w-3.5 h-3.5" />}
                    <span className="max-w-[160px] truncate">{isDetail ? tabLabel(t.n) : t.l}</span>
                    {isDetail && (
                      <Pencil
                        data-testid={`tab-rename-${t.n}`}
                        onClick={(e) => { e.stopPropagation(); setEditingTab(t.n); setTabDraft(t.n); }}
                        className={`w-3 h-3 shrink-0 ${active ? "opacity-60 hover:opacity-100" : "opacity-0 group-hover:opacity-60"}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {showSummaryPanel && (
            <div className="p-5" data-testid="boq-summary-panel">
              <div className="rounded-xl border border-border overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-900/60 px-4 py-2.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold flex justify-between">
                  <span>Section</span><span>Amount</span>
                </div>
                <div className="divide-y divide-border">
                  {detailSheets.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`summary-row-${i}`}
                      onClick={() => setActiveTab(s)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-primary/5 transition-colors text-left"
                    >
                      <span className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100 truncate">
                        <FileSpreadsheet className="w-4 h-4 text-primary shrink-0" />
                        <span className="truncate">{tabLabel(s)}</span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="font-mono">{money(sheetTotal(s), cur)}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-primary/5 border-t border-primary/20">
                  <span className="font-bold">Grand Total</span>
                  <span data-testid="summary-grand-total" className="font-mono font-bold text-primary">{money(detailGrand, cur)}</span>
                </div>
              </div>
            </div>
          )}
          {showTable && (
          <>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-4 py-3 w-8">#</th>
                  <th className="text-left font-semibold px-4 py-3 w-[280px] max-w-[280px]">BOQ Item / Matched</th>
                  <th className="text-left font-semibold px-3 py-3 w-24">Match</th>
                  <th className="text-right font-semibold px-3 py-3 w-28">Qty</th>
                  <th className="text-left font-semibold px-3 py-3 w-20">Unit</th>
                  <th className="text-right font-semibold px-3 py-3 w-32">Unit Rate</th>
                  <th className="text-right font-semibold px-4 py-3 w-32">Amount</th>
                  <th className="text-left font-semibold px-3 py-3 min-w-[150px]">Remarks</th>
                  <th className="px-3 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="line-items-body">
                {(() => {
                  const rows = [];
                  const multiSheet = !tabbed && sheetOrder.length > 1;
                  let itemNo = 0;
                  let lastKey = null;
                  let lastSheet = null;

                  const pushSubtotal = (k, tag) => {
                    if (!pageTotals.length || k === null) return;
                    const [ps, pp] = k.split("||");
                    rows.push(
                      <tr key={`sub-${tag}`} data-testid={`page-subtotal-${rows.length}`} className="bg-primary/5 border-b border-primary/20">
                        <td className="px-4 py-2.5"></td>
                        <td className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-primary" colSpan={5}>
                          Sub-total — {pp || ps || "Section"}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-primary whitespace-nowrap">
                          {money(pageSubtotal(ps, pp) || 0, cur)}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    );
                  };

                  viewLines.forEach((li, idx) => {
                    const key = `${li.sheet || ""}||${li.page || ""}`;
                    if (lastKey !== null && key !== lastKey) pushSubtotal(lastKey, `${idx}`);
                    if (multiSheet && (li.sheet || "") !== lastSheet) {
                      lastSheet = li.sheet || "";
                      rows.push(
                        <tr key={`sheet-${idx}`} data-testid={`sheet-divider-${rows.length}`} className="bg-slate-900 dark:bg-slate-800">
                          <td className="px-4 py-2 text-white text-xs font-bold uppercase tracking-widest" colSpan={9}>
                            Tab: {li.sheet}
                          </td>
                        </tr>
                      );
                    }
                    lastKey = key;

                    const kind = li.kind || "item";
                    const indent = 14 * (Number(li.level) || 0);

                    if (kind === "page") {
                      rows.push(
                        <tr key={li.id} data-testid={`line-page-${idx}`} className="bg-primary/10 border-y border-primary/20">
                          <td className="px-4 py-2.5"></td>
                          <td className="px-4 py-2.5">
                            <Input
                              data-testid={`page-desc-${idx}`}
                              value={li.boq_description || ""}
                              onChange={(e) => updateLine(li.id, { boq_description: e.target.value })}
                              className="h-8 text-sm font-bold uppercase tracking-wide text-primary bg-transparent border-transparent hover:border-border focus:border-input px-1"
                            />
                          </td>
                          <td colSpan={6}></td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1">
                              <button data-testid={`insert-line-${idx}`} title="Add an item below this line" onClick={() => insertAfter(li.id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              <button data-testid={`delete-line-${idx}`} onClick={() => removeLine(li.id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                      return;
                    }

                    if (kind === "header") {
                      rows.push(
                        <tr key={li.id} data-testid={`line-header-${idx}`} className="border-b border-border bg-slate-100/70 dark:bg-slate-900/70">
                          <td className="px-4 py-2.5"></td>
                          <td className="px-4 py-2.5">
                            <Input
                              data-testid={`header-desc-${idx}`}
                              value={li.boq_description || ""}
                              onChange={(e) => updateLine(li.id, { boq_description: e.target.value })}
                              style={{ marginLeft: Math.min(indent, 28) }}
                              title={li.boq_description || ""}
                              className="h-8 text-sm font-bold bg-transparent border-transparent hover:border-border focus:border-input px-1"
                            />
                          </td>
                          <td colSpan={6}></td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1">
                              <button data-testid={`insert-line-${idx}`} title="Add an item below this line" onClick={() => insertAfter(li.id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              <button data-testid={`delete-line-${idx}`} onClick={() => removeLine(li.id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                      return;
                    }

                    itemNo += 1;
                    const cm = confidenceMeta(li.source, li.confidence);
                    const amount = (Number(li.quantity) || 0) * (Number(li.unit_rate) || 0);
                    const missing = rateMissing(li.unit_rate);
                    rows.push(
                      <tr key={li.id} data-testid={`line-item-${idx}`} className={`border-b border-border last:border-0 align-top ${missing ? "bg-rose-50/40 dark:bg-rose-950/10" : ""}`}>
                        <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{itemNo}</td>
                        <td className="px-4 py-3 w-[280px] max-w-[280px]" style={{ paddingLeft: 16 + Math.min(indent, 28) }}>
                          {li.header_path ? (
                            <div title={li.header_path} className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-1 truncate">
                              {li.header_path.split(" > ").slice(-2).join(" › ")}
                            </div>
                          ) : null}
                          {li.boq_description ? (
                            <div className="text-xs text-muted-foreground mb-1 flex items-start gap-1 break-words">
                              <span className="uppercase tracking-wide shrink-0">BOQ:</span>
                              <span>{li.boq_description}</span>
                            </div>
                          ) : null}
                          <Input
                            data-testid={`line-desc-${idx}`}
                            value={li.matched_description || li.boq_description || ""}
                            onChange={(e) => updateLine(li.id, { matched_description: e.target.value })}
                            placeholder="Item description"
                            className="h-8 text-sm"
                          />
                          {li.item_code && <div className="text-[11px] text-muted-foreground font-mono mt-1">{li.item_code}</div>}
                          {(canAddToCatalogue(li) || savedCat[li.id]) && (
                            savedCat[li.id] ? (
                              <div data-testid={`in-catalogue-${idx}`} className="mt-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                                <Check className="w-3 h-3" /> In catalogue
                              </div>
                            ) : (
                              <button
                                data-testid={`add-to-catalogue-${idx}`}
                                onClick={() => addToCatalogue(li)}
                                title="Save this item and rate to your catalogue so it auto-matches next time"
                                className="mt-1.5 text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                              >
                                <BookOpen className="w-3 h-3" /> Add to catalogue
                              </button>
                            )
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${cm.cls}`}>{cm.label}</span>
                          <div className="mt-1.5"><MatchSwap item={li} catalogue={catalogue} onPick={pickMatch} /></div>
                        </td>
                        <td className="px-3 py-3">
                          <Input data-testid={`line-qty-${idx}`} type="number" step="0.01" value={li.quantity} onChange={(e) => updateLine(li.id, { quantity: e.target.value })} className="h-8 text-sm text-right font-mono" />
                        </td>
                        <td className="px-3 py-3">
                          <Input value={li.unit || ""} onChange={(e) => updateLine(li.id, { unit: e.target.value })} className="h-8 text-sm w-16" />
                        </td>
                        <td className="px-3 py-3">
                          <div className="relative">
                            <Input data-testid={`line-rate-${idx}`} type="number" step="0.01" value={li.unit_rate} onChange={(e) => updateLine(li.id, { unit_rate: e.target.value })} className={`h-8 text-sm text-right font-mono ${missing ? "border-rose-300 dark:border-rose-800" : ""}`} />
                            {missing && <AlertTriangle className="w-3.5 h-3.5 text-rose-500 absolute -left-4 top-2" />}
                            {li.rate_note && (
                              <div data-testid={`rate-note-${idx}`} className="mt-1 text-right text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                                {li.rate_note}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div data-testid={`line-amount-${idx}`} className="font-mono font-medium text-slate-900 dark:text-slate-100">
                            {li.rate_only ? <span className="text-muted-foreground">—</span> : money(amount, cur)}
                          </div>
                          {li.rate_only && <div className="text-[10px] text-muted-foreground mt-0.5">not in total</div>}
                        </td>
                        <td className="px-3 py-3">
                          <Input
                            data-testid={`line-remarks-${idx}`}
                            value={li.remarks || ""}
                            onChange={(e) => updateLine(li.id, { remarks: e.target.value })}
                            placeholder="Remarks"
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button data-testid={`insert-line-${idx}`} title="Add an item below this line" onClick={() => insertAfter(li.id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                            <button data-testid={`delete-line-${idx}`} onClick={() => removeLine(li.id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                  pushSubtotal(lastKey, "final");
                  return rows;
                })()}
              </tbody>
            </table>
          </div>

          {/* Mobile / tablet cards — full-width so Qty & Rate are always visible */}
          <div className="lg:hidden divide-y divide-border" data-testid="line-items-cards">
            {(() => {
              const cards = [];
              const multiSheet = (rfq.sheets?.length || 0) > 1;
              let itemNo = 0;
              let lastKey = null;
              let lastSheet = null;

              const pushSubtotal = (k, tag) => {
                if (!pageTotals.length || k === null) return;
                const [ps, pp] = k.split("||");
                cards.push(
                  <div key={`sub-${tag}`} data-testid={`page-subtotal-m-${cards.length}`} className="flex items-center justify-between gap-3 px-4 py-3 bg-primary/5">
                    <span className="text-xs font-bold uppercase tracking-wide text-primary">Sub-total — {pp || ps || "Section"}</span>
                    <span className="font-mono font-bold text-primary whitespace-nowrap">{money(pageSubtotal(ps, pp) || 0, cur)}</span>
                  </div>
                );
              };

              viewLines.forEach((li, idx) => {
                const key = `${li.sheet || ""}||${li.page || ""}`;
                if (lastKey !== null && key !== lastKey) pushSubtotal(lastKey, `${idx}`);
                if (multiSheet && (li.sheet || "") !== lastSheet) {
                  lastSheet = li.sheet || "";
                  cards.push(
                    <div key={`sheet-${idx}`} className="px-4 py-2 bg-slate-900 dark:bg-slate-800 text-white text-xs font-bold uppercase tracking-widest">
                      Tab: {li.sheet}
                    </div>
                  );
                }
                lastKey = key;

                const kind = li.kind || "item";

                if (kind === "page" || kind === "header") {
                  cards.push(
                    <div key={li.id} data-testid={`line-${kind}-m-${idx}`} className={`px-4 py-3 flex items-center gap-2 ${kind === "page" ? "bg-primary/10" : "bg-slate-100/70 dark:bg-slate-900/70"}`}>
                      <Input
                        data-testid={`${kind}-desc-m-${idx}`}
                        value={li.boq_description || ""}
                        onChange={(e) => updateLine(li.id, { boq_description: e.target.value })}
                        className={`h-9 flex-1 font-bold bg-transparent border-transparent hover:border-border focus:border-input px-1 ${kind === "page" ? "text-primary uppercase tracking-wide" : ""}`}
                      />
                      <button data-testid={`insert-line-m-${idx}`} title="Add an item below" onClick={() => insertAfter(li.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 shrink-0">
                        <Plus className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-line-m-${idx}`} onClick={() => removeLine(li.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                  return;
                }

                itemNo += 1;
                const cm = confidenceMeta(li.source, li.confidence);
                const amount = (Number(li.quantity) || 0) * (Number(li.unit_rate) || 0);
                const missing = rateMissing(li.unit_rate);
                cards.push(
                  <div key={li.id} data-testid={`line-item-m-${idx}`} className={`p-4 space-y-3 ${missing ? "bg-rose-50/40 dark:bg-rose-950/10" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-muted-foreground">{itemNo}</span>
                        <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${cm.cls}`}>{cm.label}</span>
                        <MatchSwap item={li} catalogue={catalogue} onPick={pickMatch} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`insert-line-m-${idx}`} title="Add an item below" onClick={() => insertAfter(li.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10">
                          <Plus className="w-4 h-4" />
                        </button>
                        <button data-testid={`delete-line-m-${idx}`} onClick={() => removeLine(li.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {li.header_path ? (
                      <div title={li.header_path} className="text-[10px] uppercase tracking-wide text-muted-foreground/70 truncate">
                        {li.header_path.split(" > ").slice(-2).join(" › ")}
                      </div>
                    ) : null}
                    {li.boq_description ? (
                      <div className="text-xs text-muted-foreground flex items-start gap-1 break-words">
                        <span className="uppercase tracking-wide shrink-0">BOQ:</span>
                        <span>{li.boq_description}</span>
                      </div>
                    ) : null}
                    <Input
                      data-testid={`line-desc-m-${idx}`}
                      value={li.matched_description || li.boq_description || ""}
                      onChange={(e) => updateLine(li.id, { matched_description: e.target.value })}
                      placeholder="Item description"
                      className="h-9 text-sm"
                    />
                    {li.item_code && <div className="text-[11px] text-muted-foreground font-mono">{li.item_code}</div>}
                    {(canAddToCatalogue(li) || savedCat[li.id]) && (
                      savedCat[li.id] ? (
                        <div data-testid={`in-catalogue-m-${idx}`} className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                          <Check className="w-3 h-3" /> In catalogue
                        </div>
                      ) : (
                        <button data-testid={`add-to-catalogue-m-${idx}`} onClick={() => addToCatalogue(li)} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> Add to catalogue
                        </button>
                      )
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Qty</Label>
                        <Input data-testid={`line-qty-m-${idx}`} type="number" step="0.01" value={li.quantity} onChange={(e) => updateLine(li.id, { quantity: e.target.value })} className="h-9 text-sm text-right font-mono mt-1" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Unit</Label>
                        <Input value={li.unit || ""} onChange={(e) => updateLine(li.id, { unit: e.target.value })} className="h-9 text-sm mt-1" />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Unit Rate</Label>
                        <Input data-testid={`line-rate-m-${idx}`} type="number" step="0.01" value={li.unit_rate} onChange={(e) => updateLine(li.id, { unit_rate: e.target.value })} className={`h-9 text-sm text-right font-mono mt-1 ${missing ? "border-rose-300 dark:border-rose-800" : ""}`} />
                        {li.rate_note && <div className="mt-1 text-right text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">{li.rate_note}</div>}
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground">Amount</Label>
                        <div data-testid={`line-amount-m-${idx}`} className="h-9 mt-1 flex items-center justify-end font-mono font-medium text-slate-900 dark:text-slate-100">
                          {li.rate_only ? <span className="text-muted-foreground">—</span> : money(amount, cur)}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[11px] text-muted-foreground">Remarks</Label>
                      <Input data-testid={`line-remarks-m-${idx}`} value={li.remarks || ""} onChange={(e) => updateLine(li.id, { remarks: e.target.value })} placeholder="Remarks" className="h-9 text-sm mt-1" />
                    </div>
                  </div>
                );
              });
              pushSubtotal(lastKey, "final");
              return cards;
            })()}
          </div>
          </>
          )}
          </>
        )}
      </div>

      {/* Totals + Quotation supplements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden" data-testid="quotation-supplements">
            <div className="flex flex-wrap items-center gap-3 px-5 py-4">
              <button
                data-testid="supplements-toggle"
                onClick={() => setSupplementsOpen((o) => !o)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
              >
                <Layers className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900 dark:text-slate-100">Quotation supplements</div>
                  <div className="text-[11px] text-muted-foreground truncate">Notes, terms, quotation details and cover &amp; closing pages</div>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${supplementsOpen ? "rotate-180" : ""}`} />
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <Select value="" onValueChange={applyTemplate} disabled={applyingTpl || templates.length === 0}>
                  <SelectTrigger data-testid="apply-template-select" className="h-9 w-[180px] text-xs">
                    {applyingTpl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SelectValue placeholder={templates.length ? "Autofill from template" : "No templates yet"} />}
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`apply-template-${t.id}`}>
                        {t.name}{t.is_default ? " · default" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" data-testid="save-as-template-button" onClick={() => setSaveTplOpen(true)} className="h-9 gap-1.5 text-xs">
                  <FilePlus2 className="w-3.5 h-3.5" /> Save as template
                </Button>
              </div>
            </div>

            {supplementsOpen && (
            <div className="border-t border-border p-5 space-y-5" data-testid="supplements-body">
              <div data-testid="quote-settings" className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Tax %</Label>
                  <Input data-testid="tax-input" type="number" step="0.01" value={meta.tax_percent} onChange={(e) => setMeta({ ...meta, tax_percent: e.target.value })} className="mt-2 font-mono" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Validity (days)</Label>
                  <Input data-testid="validity-input" type="number" value={meta.validity_days} onChange={(e) => setMeta({ ...meta, validity_days: parseInt(e.target.value) || 0 })} className="mt-2 font-mono" />
                </div>
              </div>
              <div className="border-t border-border pt-5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Notes</Label>
                <Textarea data-testid="quote-notes-input" value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} rows={2} className="mt-2" placeholder="Internal or contractor-facing notes for this quote" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Terms & Conditions</Label>
                <Textarea data-testid="quote-terms-input" value={meta.terms} onChange={(e) => setMeta({ ...meta, terms: e.target.value })} rows={4} className="mt-2" />
              </div>
              <div className="space-y-4 pt-1 border-t border-border" data-testid="quotation-details-card">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold pt-3">Quotation Details</div>
                <div>
                  <Label className="text-xs">Intro / cover note to client</Label>
                  <Textarea data-testid="quote-intro-input" value={meta.intro} onChange={(e) => setMeta({ ...meta, intro: e.target.value })} rows={3} className="mt-1.5" placeholder="We are pleased to submit our quotation for…" />
                </div>
                <div>
                  <Label className="text-xs">Payment terms</Label>
                  <Textarea data-testid="quote-payment-terms-input" value={meta.payment_terms} onChange={(e) => setMeta({ ...meta, payment_terms: e.target.value })} rows={2} className="mt-1.5" placeholder="e.g. 50% advance, balance on delivery" />
                </div>
                <div>
                  <Label className="text-xs">Bank details (for payment)</Label>
                  <Textarea data-testid="quote-bank-details-input" value={meta.bank_details} onChange={(e) => setMeta({ ...meta, bank_details: e.target.value })} rows={3} className="mt-1.5" placeholder="Bank name, account name, IBAN, account no." />
                </div>
              </div>
              <div data-testid="cover-page-card" className="pt-1 border-t border-border">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold pt-3 block">Cover &amp; closing pages</Label>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Wrapped around this quotation wherever it goes — the PDF you download, the copy the
                  contractor opens and the submitted quote.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {[
                    { v: "default", label: "Use my saved pages" },
                    { v: "custom", label: "Just for this quote" },
                    { v: "none", label: "No extra pages" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      data-testid={`cover-mode-${o.v}`}
                      onClick={() => setMeta({ ...meta, cover_mode: o.v })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                        (meta.cover_mode || "default") === o.v
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/40 text-muted-foreground"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {(meta.cover_mode || "default") === "custom" ? (
                  <div className="mt-4">
                    <QuotePdfPages
                      scope={id}
                      value={rfq}
                      onChange={() => api.get(`/rfqs/${id}`).then((r) => setRfq(r.data))}
                      testid="quote-pdf"
                    />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-3">
                    {(meta.cover_mode || "default") === "default"
                      ? ((rfq?.cover_pdf || rfq?.closing_pdf)
                          ? `Using ${[rfq?.cover_pdf && "a cover page", rfq?.closing_pdf && "a closing page"].filter(Boolean).join(" and ")} from your template.`
                          : "No cover/closing pages — add them via a Quotation Template, or pick “Just for this quote”.")
                      : "This quotation will go out as the Matiqs pages only."}
                  </p>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm h-fit sticky top-24">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">Quotation Summary</div>
          {pageTotals.length > 0 && (
            <div data-testid="collection-summary" className="mb-4 pb-4 border-b border-border space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Collection</div>
              {pageTotals.map((p, i) => (
                <div key={i} className="flex justify-between text-xs gap-2">
                  <span className="text-muted-foreground truncate" title={`${p.sheet} ${p.page}`}>
                    {p.page || p.sheet || "—"}
                  </span>
                  <span data-testid={`collection-row-${i}`} className="font-mono shrink-0">{money(p.subtotal, cur)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span data-testid="summary-subtotal" className="font-mono font-medium">{money(totals.sub, cur)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">Discount</span>
                <Select value={meta.discount_type} onValueChange={(v) => setMeta({ ...meta, discount_type: v })}>
                  <SelectTrigger data-testid="discount-type-select" className="h-7 w-16 text-xs bg-card px-2"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="percent">%</SelectItem>
                    <SelectItem value="amount">{cur}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                data-testid="discount-input"
                type="number"
                step="0.01"
                value={meta.discount_value}
                onChange={(e) => setMeta({ ...meta, discount_value: e.target.value })}
                className="h-7 w-24 text-right font-mono text-sm"
              />
            </div>
            {totals.discount > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount applied</span>
                  <span data-testid="summary-discount" className="font-mono font-medium text-emerald-600 dark:text-emerald-400">
                    - {money(totals.discount, cur)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Net Amount</span>
                  <span data-testid="summary-net" className="font-mono font-medium">{money(totals.net, cur)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tax ({parseFloat(meta.tax_percent) || 0}%)</span>
              <span data-testid="summary-tax" className="font-mono font-medium">{money(totals.tax, cur)}</span>
            </div>
            <div className="border-t border-border pt-3 flex justify-between items-center">
              <span className="font-semibold">Grand Total</span>
              <span data-testid="summary-total" className="font-mono font-bold text-lg text-primary">{money(totals.total, cur)}</span>
            </div>
          </div>
          {revisions.length > 0 && (
            <div data-testid="revisions-panel" className="mt-5 pt-4 border-t border-border">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Previous Revisions</div>
              <div className="space-y-1.5">
                {revisions.map((r) => (
                  <button
                    key={r.revision}
                    data-testid={`revision-pdf-${r.revision}`}
                    onClick={() => openDownload(`/rfqs/${id}/revisions/${r.revision}/pdf`)}
                    className="w-full flex items-center justify-between gap-2 text-xs px-2.5 py-2 rounded-lg bg-secondary hover:bg-secondary/70 transition-colors"
                  >
                    <span className="flex items-center gap-1.5 font-semibold">
                      <FileText className="w-3.5 h-3.5" /> Rev {r.revision}
                    </span>
                    <span className="font-mono text-muted-foreground">{money(r.total, cur)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {missingRates > 0 && (
            <div className="mt-4 flex items-start gap-2 text-xs text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/20 rounded-lg p-2.5">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              {missingRates} item(s) have no rate. Set rates before sending the quote.
            </div>
          )}
          <Button data-testid="save-quotation-summary-button" onClick={() => save()} disabled={saving} variant="outline" className="w-full mt-5 gap-2 h-11 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Quotation
          </Button>
          {buyerRfqId && (
            <Button data-testid="send-quotation-summary-button" onClick={requestSend} disabled={sending || saving} className="w-full mt-3 gap-2 h-11 font-semibold">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Quotation
            </Button>
          )}
          <Button data-testid="download-pdf-button-2" onClick={downloadPdf} variant="outline" className="w-full mt-3 gap-2 h-11 font-semibold">
            <Download className="w-4 h-4" /> Download PDF Quote
          </Button>
          <Button data-testid="preview-quotation-button-2" onClick={previewQuotation} variant="outline" className="w-full mt-3 gap-2 h-11 font-semibold">
            <FileText className="w-4 h-4" /> View / Print Quotation
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="send-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this quotation?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {quoteWarnings().length > 0 ? (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-2">
                    <div className="flex items-center gap-2 font-semibold text-amber-800 mb-1">
                      <AlertTriangle className="w-4 h-4" /> Please review before sending
                    </div>
                    <ul className="list-disc pl-5 text-sm text-amber-800 space-y-0.5">
                      {quoteWarnings().map((w, i) => <li key={i} data-testid={`send-warning-${i}`}>{w}</li>)}
                    </ul>
                  </div>
                ) : (
                  <span>Everything looks complete. The quotation will be sent to the contractor.</span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="send-cancel-button">Review again</AlertDialogCancel>
            <AlertDialogAction data-testid="send-confirm-button" onClick={confirmSend}>Send anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save-as-template dialog */}
      <Dialog open={saveTplOpen} onOpenChange={(o) => { if (!savingTpl) setSaveTplOpen(o); }}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>Save as a quotation template</DialogTitle>
            <DialogDescription>
              Saves this quote's notes, terms, quotation details and cover/closing pages as a reusable template you can autofill on any future quote.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="new-tpl-name">Template name</Label>
            <Input id="new-tpl-name" data-testid="new-template-name-input" value={newTplName}
                   onChange={(e) => setNewTplName(e.target.value)} className="mt-1.5"
                   placeholder="e.g. Civil works — standard T&C" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplOpen(false)} disabled={savingTpl}>Cancel</Button>
            <Button data-testid="confirm-save-template-button" onClick={saveAsTemplate} disabled={savingTpl} className="gap-2">
              {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus2 className="w-4 h-4" />} Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quotation preview modal */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent data-testid="quotation-preview-dialog" className="max-w-4xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-3 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base"><Eye className="w-4 h-4" /> Quotation preview</DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-secondary min-h-0">
            {previewLoading || !previewUrl ? (
              <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">Building your quotation…</span>
              </div>
            ) : (
              <iframe data-testid="quotation-preview-frame" title="Quotation preview" src={previewUrl} className="w-full h-full border-0" />
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-border">
            <Button variant="outline" onClick={closePreview}>Close</Button>
            <Button onClick={downloadPdf} className="gap-2"><Download className="w-4 h-4" /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
